import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CHARGER_STATUS,
  OCPP_STATUS,
  OUT_OF_ORDER_PORT_OCPP_STATUS,
} from '../../constant/Text.constant';
import { ChargerPort } from '../../stores/types/sessions.interface';
import { convertToDate, getDifferenceInMinutes } from '../../utils/Date.Util';
import { handleTimeCalculation } from '../Session/utils';
import { ColorType } from '../_ui';
import { Label, LabelType } from '../_ui/Label.component';
import { Pill } from '../_ui/Pill.component';
import { Tooltip, TooltipContent, TooltipTrigger } from '../_ui/Tooltip.component';
import StatusChip from './StatusChip.component';
import { copyChargingToInuse, getValidRawStatus } from './utils';

interface InputProps {
  status?: string;
  ports?: ChargerPort[];
  isLoading?: boolean;
  timeStamp?: number | string;
}

export const ChargerStatus = memo(
  ({ status = '', ports = [], isLoading = false, timeStamp }: InputProps) => {
    const timeStampText = timeStamp ? `(~${handleTimeCalculation(timeStamp)})` : '';
    const { t } = useTranslation();
    const getStatusProperty = (chargerStatus: string) => {
      let color;
      let tooltip;
      let secondaryColor = ColorType.POSITIVE0;
      let secondaryTextColor;
      let textColor = ColorType.WHITE;
      switch (chargerStatus) {
        case CHARGER_STATUS.COMING_SOON:
          color = ColorType.GREY5;
          secondaryColor = ColorType.WHITE;
          secondaryTextColor = ColorType.GREY5;
          tooltip = 'coming_soon_tooltip';
          break;
        case CHARGER_STATUS.AVAILABLE:
          color = ColorType.POSITIVE;
          secondaryColor = ColorType.WHITE;
          secondaryTextColor = ColorType.POSITIVE;
          tooltip = 'available_status_tooltip';
          break;
        case OCPP_STATUS.CHARGING:
          color = ColorType.DENIM;
          secondaryColor = ColorType.WHITE;
          tooltip = 'charging_status_tooltip';
          break;
        case CHARGER_STATUS.SCHEDULED:
          color = ColorType.PURPLE;
          tooltip = '';
          break;
        case CHARGER_STATUS.OFFLINE:
          color = ColorType.WARNING;
          textColor = ColorType.BLACK;
          secondaryColor = ColorType.WHITE;
          secondaryTextColor = ColorType.BLACK;
          tooltip = 'offline_status_tooltip';
          break;
        case CHARGER_STATUS.OUT_OF_ORDER:
          color = ColorType.NEGATIVE;
          secondaryColor = ColorType.NEGATIVE0;
          tooltip = 'outoforder_status_tooltip';
          break;
        case OCPP_STATUS.SUSPENDED_EV:
          color = ColorType.DENIM;
          secondaryColor = ColorType.WHITE;
          tooltip = 'suspendedev_status_tooltip';
          break;
        case OCPP_STATUS.SUSPENDED_EVSE:
          color = ColorType.DENIM;
          secondaryColor = ColorType.WHITE;
          tooltip = 'suspendedevse_status_tooltip';
          break;
        case OCPP_STATUS.FINISHING:
          color = ColorType.DENIM;
          secondaryColor = ColorType.WHITE;
          tooltip = 'finishing_status_tooltip';
          break;
        case OUT_OF_ORDER_PORT_OCPP_STATUS.UNAVAILABLE:
          color = ColorType.NEGATIVE;
          tooltip = 'outoforder_status_tooltip';
          break;
        case OUT_OF_ORDER_PORT_OCPP_STATUS.FAULTED:
          color = ColorType.NEGATIVE;
          tooltip = 'outoforder_status_tooltip';
          break;
        default:
          color = ColorType.POSITIVE;
          secondaryColor = ColorType.WHITE;
          tooltip = '';
          break;
      }
      return { color, textColor, tooltip, secondaryColor, secondaryTextColor };
    };

    const { color, tooltip, secondaryColor, textColor, secondaryTextColor } =
      getStatusProperty(status);

    const inUseArray = [
      OCPP_STATUS.CHARGING,
      OCPP_STATUS.SUSPENDED_EV,
      OCPP_STATUS.SUSPENDED_EVSE,
      OCPP_STATUS.FINISHING,
    ];

    let secondaryText = t(status);
    if (ports.length === 1) {
      secondaryText = t('in_use');
    } else if (ports.length > 1) {
      secondaryText = `${ports.length} ${t('ports')}`;
    }
    const renderStatus =
      inUseArray.includes(status) || ports.length > 1 ? (
        <StatusChip
          isLoading={isLoading}
          primaryText={inUseArray.includes(status) ? t('in_use') : t(status)}
          secondaryText={secondaryText}
          primaryTextColor={textColor}
          primaryBgColor={color}
          secondaryTextColor={secondaryTextColor || color}
          secondaryBgColor={secondaryColor}
        />
      ) : (
        <Pill
          label={t(copyChargingToInuse(status))}
          bgColor={color}
          isLoading={isLoading}
          labelColor={textColor}
        />
      );

    return tooltip || ports.length > 1 ? (
      <Tooltip
        placement='top'
        tipOffset={{
          mainAxis: 10,
          crossAxis: -10,
        }}
      >
        <TooltipTrigger>{renderStatus}</TooltipTrigger>
        <TooltipContent>
          {ports.length > 1 ? (
            ports.map((port, i) => {
              const timeStampData = `(~${handleTimeCalculation(
                getDifferenceInMinutes(
                  new Date(),
                  convertToDate(port?.rawChargerStatus?.rawChargerStatusChangedTime),
                ),
              )})`;

              const portStatus =
                port.status.toLowerCase() === OCPP_STATUS.CHARGING
                  ? getValidRawStatus(
                      port?.rawChargerStatus?.rawChargerStatus?.toLowerCase() ||
                        port.status.toLowerCase(),
                    )
                  : port.status.toLowerCase();

              const { tooltip: portTooltip } = getStatusProperty(portStatus);
              return (
                <Label
                  key={portTooltip}
                  text={`${t('port')} ${i + 1}:  ${t(portTooltip, {
                    timeStampText: portStatus !== CHARGER_STATUS.AVAILABLE ? timeStampData : '',
                  })}`}
                  type={LabelType.LABEL_S_MEDIUM}
                  color={ColorType.WHITE}
                  className={`${i > 0 ? 'mt-2' : ''}`}
                />
              );
            })
          ) : (
            <Label
              text={t(tooltip, { timeStampText })}
              type={LabelType.LABEL_S_MEDIUM}
              color={ColorType.WHITE}
            />
          )}
        </TooltipContent>
      </Tooltip>
    ) : (
      renderStatus
    );
  },
);

ChargerStatus.displayName = 'ChargerStatus';
